import React, { useEffect, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Stack,
  Alert,
  Typography,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  CircularProgress
} from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { default as api } from '../../../services/api';
import { useAuth } from '../../../context/AuthContext';

const CreateAerialInspectionModal = ({ open, onClose, onSuccess }) => {
  const { getUserOrganization } = useAuth();
  const organizationId = getUserOrganization()?.id;

  const [loading, setLoading] = useState(false);
  const [loadingResources, setLoadingResources] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  const [inspectionTypes, setInspectionTypes] = useState([]);
  const [inspectors, setInspectors] = useState([]);
  const [selectedInspectionType, setSelectedInspectionType] = useState(null);

  const [formData, setFormData] = useState({
    title: '',
    inspection_type_id: '',
    inspector_id: '',
    inspection_date: new Date(),
    notes: '',
    cycle: ''
  });

  useEffect(() => {
    if (!open || !organizationId) return;
    const fetchResources = async () => {
      setLoadingResources(true);
      setError(null);
      try {
        // load inspection types for organization, scoped to road/bridge category if needed later
        const typesResp = await api.get('/inspections/types', {
          params: { organization_id: organizationId, asset_type_id: 1 }
        });
        if (Array.isArray(typesResp.data)) setInspectionTypes(typesResp.data);

        const inspectorsResp = await api.get('/users', {
          params: { role: 'inspector', organization_id: organizationId }
        });
        if (Array.isArray(inspectorsResp.data)) setInspectors(inspectorsResp.data);
      } catch (err) {
        let msg = 'Failed to load inspection types or inspectors';
        if (err.response) msg += `: ${err.response.status} - ${err.response.data?.error || ''}`;
        setError(msg);
      } finally {
        setLoadingResources(false);
      }
    };
    // reset form each open
    setFormData({ title: '', inspection_type_id: '', inspector_id: '', inspection_date: new Date(), notes: '', cycle: '' });
    setSelectedInspectionType(null);
    setSuccess(false);
    fetchResources();
  }, [open, organizationId]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === 'inspection_type_id') {
      const found = inspectionTypes.find(t => t.id === value);
      setSelectedInspectionType(found || null);
      setFormData(prev => ({ ...prev, [name]: value, cycle: '' }));
      return;
    }
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const isFormValid = () => {
    return Boolean(
      organizationId &&
      formData.title.trim() &&
      formData.inspection_type_id &&
      formData.inspector_id &&
      formData.inspection_date &&
      formData.cycle
    );
  };

  const handleSubmit = async () => {
    if (!organizationId) {
      setError('No organization found for current user');
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const payload = {
        organization_id: organizationId,
        inspector_id: formData.inspector_id,
        inspection_date: formData.inspection_date,
        title: formData.title,
        notes: formData.notes,
        status: 'draft',
        inspection_type_id: formData.inspection_type_id,
        metadata: {
          cycle: formData.cycle
        }
      };
      const resp = await api.post('/aerial-inspections', payload);
      setSuccess(true);
      if (onSuccess) onSuccess(resp.data);
      setTimeout(() => onClose(), 800);
    } catch (err) {
      let msg = 'Failed to create aerial inspection';
      if (err.response) msg = `API Error: ${err.response.status} - ${err.response.data?.error || ''}`;
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  const generateCycleOptions = () => {
    // Keep it simple: weekly cycles 1-52 for now
    const options = [];
    for (let i = 1; i <= 52; i++) options.push(
      <MenuItem key={i} value={i.toString()}>Week {i}</MenuItem>
    );
    return options;
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ bgcolor: 'primary.main', color: 'primary.contrastText', mb: 2 }}>
        Create New Aerial Inspection
      </DialogTitle>
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>
        )}
        {success && (
          <Alert severity="success" sx={{ mb: 2 }}>Aerial inspection created successfully!</Alert>
        )}
        {loadingResources ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
            <CircularProgress size={40} />
          </Box>
        ) : (
          <Stack spacing={3} sx={{ mt: 1 }}>
            <Typography variant="subtitle1" fontWeight="bold">
              Inspection Details
            </Typography>

            <FormControl fullWidth>
              <InputLabel id="inspection-type-label">Inspection Type</InputLabel>
              <Select
                labelId="inspection-type-label"
                id="inspection_type_id"
                name="inspection_type_id"
                value={formData.inspection_type_id}
                onChange={handleChange}
                label="Inspection Type"
              >
                {inspectionTypes.length > 0 ? (
                  inspectionTypes.map((type) => (
                    <MenuItem key={type.id} value={type.id}>
                      {type.name}
                    </MenuItem>
                  ))
                ) : (
                  <MenuItem disabled value="">
                    No inspection types available
                  </MenuItem>
                )}
              </Select>
            </FormControl>

            <TextField
              id="title"
              name="title"
              label="Inspection Title"
              value={formData.title}
              onChange={handleChange}
              fullWidth
              variant="outlined"
            />

            <FormControl fullWidth>
              <InputLabel id="inspector-label">Inspector</InputLabel>
              <Select
                labelId="inspector-label"
                id="inspector_id"
                name="inspector_id"
                value={formData.inspector_id}
                onChange={handleChange}
                label="Inspector"
              >
                {inspectors.length > 0 ? (
                  inspectors.map((inspector) => (
                    <MenuItem key={inspector.id} value={inspector.id}>
                      {inspector.full_name || inspector.username}
                    </MenuItem>
                  ))
                ) : (
                  <MenuItem disabled value="">
                    No inspectors available
                  </MenuItem>
                )}
              </Select>
            </FormControl>

            <FormControl fullWidth>
              <InputLabel id="cycle-label">Inspection Cycle</InputLabel>
              <Select
                labelId="cycle-label"
                id="cycle"
                name="cycle"
                value={formData.cycle}
                onChange={handleChange}
                label="Inspection Cycle"
              >
                {generateCycleOptions()}
              </Select>
            </FormControl>

            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <DatePicker
                label="Inspection Date"
                value={formData.inspection_date}
                onChange={(newDate) => setFormData(prev => ({ ...prev, inspection_date: newDate }))}
                slotProps={{ textField: { fullWidth: true, variant: 'outlined' } }}
              />
            </LocalizationProvider>

            <TextField
              id="notes"
              name="notes"
              label="Notes"
              multiline
              rows={4}
              value={formData.notes}
              onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
              fullWidth
              variant="outlined"
            />
          </Stack>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2, bgcolor: 'background.paper', borderTop: 1, borderColor: 'divider' }}>
        <Button onClick={onClose} color="inherit" disabled={loading} variant="outlined">
          Cancel
        </Button>
        <Button onClick={handleSubmit} color="primary" variant="contained" disabled={!isFormValid() || loading || loadingResources} sx={{ px: 3 }}>
          {loading ? <CircularProgress size={24} /> : 'Create Inspection'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CreateAerialInspectionModal;


